// Shared validation rules and patterns for form validation
// Used by both frontend and backend (conceptually aligned)

/**
 * Field length limits
 */
export const FIELD_LIMITS = {
  companyName: 100,
  companySupplement: 100,
  street: 100,
  houseNumber: 10,
  postalCode: 10,
  city: 50,
  country: 50,
  phone: 20,
  firstName: 50,
  lastName: 50,
  email: 100,
  salutation: 20
};

/**
 * Validation patterns for different field types
 */
export const VALIDATION_PATTERNS = {
  // Email pattern - basic RFC compliant
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  // Phone pattern - allows international format
  phone: /^\+?[0-9\s\-()]{6,20}$/,

  // Postal code patterns by country
  postalCodeDE: /^\d{5}$/,        // Germany: 5 digits
  postalCodeCH: /^\d{4}$/,        // Switzerland: 4 digits
  postalCodeAT: /^\d{4}$/,        // Austria: 4 digits

  // Name patterns - allows letters, spaces, hyphens, apostrophes
  name: /^[a-zA-ZäöüÄÖÜß\s\-']{1,50}$/,

  // House number - alphanumeric with optional letters
  houseNumber: /^[0-9]{1,5}[a-zA-Z]?$/
};

/**
 * Validates email format
 */
export function validateEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  return VALIDATION_PATTERNS.email.test(email.trim());
}

/**
 * Validates phone number format
 */
export function validatePhone(phone: string): boolean {
  if (!phone || typeof phone !== 'string') return false;
  return VALIDATION_PATTERNS.phone.test(phone.trim());
}

/**
 * Validates postal code based on country
 */
export function validatePostalCode(code: string, country: string): boolean {
  if (!code || typeof code !== 'string') return false;

  const trimmedCode = code.trim();

  // German postal codes
  if (country === 'Deutschland' || country === 'Germany') {
    return VALIDATION_PATTERNS.postalCodeDE.test(trimmedCode);
  }

  // Swiss postal codes
  if (country === 'Schweiz' || country === 'Switzerland') {
    return VALIDATION_PATTERNS.postalCodeCH.test(trimmedCode);
  }

  // Austrian postal codes
  if (country === 'Oesterreich' || country === 'Österreich' || country === 'Austria') {
    return VALIDATION_PATTERNS.postalCodeAT.test(trimmedCode);
  }

  // For other countries, just check length
  return trimmedCode.length >= 3 && trimmedCode.length <= 10;
}

/**
 * Validates text doesn't exceed maximum length
 */
export function validateMaxLength(text: string, maxLength: number): boolean {
  return !text || text.length <= maxLength;
}

/**
 * Validates text meets minimum length
 */
export function validateMinLength(text: string, minLength: number): boolean {
  return !!text && text.length >= minLength;
}

/**
 * Validates required field
 */
export function validateRequired(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return true;
  return false;
}

/**
 * Get error message for invalid postal code based on country
 */
export function getPostalCodeError(country: string, lang: 'de' | 'en'): string {
  if (country === 'Deutschland' || country === 'Germany') {
    return lang === 'de'
      ? 'PLZ muss 5 Ziffern haben'
      : 'Postal code must be 5 digits';
  }

  if (country === 'Schweiz' || country === 'Switzerland') {
    return lang === 'de'
      ? 'PLZ muss 4 Ziffern haben'
      : 'Postal code must be 4 digits';
  }

  if (country === 'Oesterreich' || country === 'Österreich' || country === 'Austria') {
    return lang === 'de'
      ? 'PLZ muss 4 Ziffern haben'
      : 'Postal code must be 4 digits';
  }

  return lang === 'de'
    ? 'Ungültige Postleitzahl'
    : 'Invalid postal code';
}

/**
 * Sanitize input to prevent XSS
 * This is a basic HTML escape function
 */
export function escapeHtml(text: string): string {
  if (!text || typeof text !== 'string') return '';

  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };

  return text.replace(/[&<>"'`=/]/g, (char) => htmlEscapeMap[char] || char);
}